import { useEffect, useState } from "react";
import api from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend, CartesianGrid } from "recharts";
import { TrendingUp, Users, Target, Percent } from "lucide-react";

const COLORS = ["#0EA5E9", "#F97316", "#10B981", "#8B5CF6", "#F59E0B", "#EC4899", "#6366F1", "#EF4444"];

export default function AdminAnalytics() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(({ data }) => setA(data)); }, []);
  if (!a) return <div className="p-8">Loading...</div>;

  const cards = [
    { label: "Total Leads", value: a.total_leads, icon: Users, color: "sky" },
    { label: "Converted", value: a.converted, icon: Target, color: "emerald" },
    { label: "Conversion Rate", value: `${a.conversion_rate}%`, icon: Percent, color: "orange" },
    { label: "Last 30 days", value: a.by_day.reduce((s, d) => s + d.leads, 0), icon: TrendingUp, color: "violet" },
  ];

  return (
    <div className="p-8">
      <h1 className="font-display font-black text-3xl" data-testid="admin-analytics-title">Analytics</h1>
      <p className="text-neutral-500 mt-1">Lead performance and conversion insights</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        {cards.map((c, i) => (
          <div key={i} className="bg-white rounded-2xl p-6 border border-neutral-200" data-testid={`analytics-card-${i}`}>
            <div className={`w-10 h-10 rounded-xl bg-${c.color}-100 flex items-center justify-center mb-3`}>
              <c.icon className={`w-5 h-5 text-${c.color}-600`} />
            </div>
            <div className="text-3xl font-display font-black">{c.value}</div>
            <div className="text-xs text-neutral-500 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-neutral-200" data-testid="chart-by-day">
          <h3 className="font-display font-bold mb-4">Leads over last 30 days</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={a.by_day}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} interval={4} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="leads" stroke="#0EA5E9" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-neutral-200" data-testid="chart-by-source">
          <h3 className="font-display font-bold mb-4">Lead Sources</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={a.by_source} dataKey="count" nameKey="source" cx="50%" cy="50%" outerRadius={80} label={{ fontSize: 11 }}>
                {a.by_source.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-3 space-y-1 text-xs">
            {a.by_source.map((s, i) => (
              <div key={i} className="flex justify-between">
                <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }}></span>{s.source}</span>
                <span className="font-bold">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="bg-white rounded-2xl p-6 border border-neutral-200" data-testid="chart-by-course">
          <h3 className="font-display font-bold mb-4">Top Courses (by lead interest)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={a.by_course} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="course" tick={{ fontSize: 11 }} width={120} />
              <Tooltip />
              <Bar dataKey="count" fill="#0EA5E9" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-neutral-200" data-testid="chart-by-status">
          <h3 className="font-display font-bold mb-4">Leads by Status</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={a.by_status}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="status" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
